// Mocks for /api/secure/me/medicines/*. Wire shape mirrors Java DTOs
// (MedicineSummary, MedicineDoseEntry, etc.).

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.medicines.v1'

function todayAt(hh, mm = 0) {
  const d = new Date()
  d.setHours(hh, mm, 0, 0)
  return d.toISOString()
}

function seedFor(profile) {
  if (!profile) return { schedules: [], intakes: [], sideEffects: [] }
  const isSelf = profile.relation === 'SELF'

  const schedules = isSelf
    ? [
        {
          id: 'med_ssri',
          name: 'Sertraline',
          dosage: '50mg',
          form: 'TABLET',
          instructions: 'with food',
          frequencyType: 'DAILY',
          timesOfDay: ['08:00', '20:00'],
          daysOfWeek: null,
          startDate: '2026-01-12',
          endDate: null,
          prescribedBy: 'Dr. Meera Iyer',
          isActive: true,
        },
        {
          id: 'med_b12',
          name: 'Vitamin B12',
          dosage: '1 tablet',
          form: 'TABLET',
          instructions: null,
          frequencyType: 'DAILY',
          timesOfDay: ['08:00'],
          daysOfWeek: null,
          startDate: '2026-03-01',
          endDate: null,
          prescribedBy: 'self',
          isActive: true,
        },
      ]
    : [
        {
          id: 'med_metformin',
          name: 'Metformin',
          dosage: '500mg',
          form: 'TABLET',
          instructions: 'after meals',
          frequencyType: 'DAILY',
          timesOfDay: ['08:30', '20:30'],
          daysOfWeek: null,
          startDate: '2024-08-01',
          endDate: null,
          prescribedBy: 'Dr. Rao',
          isActive: true,
        },
        {
          id: 'med_amlodipine',
          name: 'Amlodipine',
          dosage: '5mg',
          form: 'TABLET',
          instructions: null,
          frequencyType: 'DAILY',
          timesOfDay: ['08:30'],
          daysOfWeek: null,
          startDate: '2024-09-12',
          endDate: null,
          prescribedBy: 'Dr. Rao',
          isActive: true,
        },
      ]

  return { schedules, intakes: [], sideEffects: [] }
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}
function writeStore(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch (e) {
    /* ignore */
  }
}
function ensure(profile) {
  const store = readStore()
  if (!store[profile.id]) {
    store[profile.id] = seedFor(profile)
    writeStore(store)
  }
  return { store, prof: store[profile.id] }
}

export async function listMedicines(profile, includeArchived = false) {
  await delay()
  if (!profile) return []
  const { prof } = ensure(profile)
  return includeArchived ? prof.schedules : prof.schedules.filter((s) => s.isActive)
}

export async function getMedicine(profile, id) {
  await delay()
  if (!profile) return null
  const { prof } = ensure(profile)
  return prof.schedules.find((s) => s.id === id) || null
}

export async function createMedicine(profile, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  const next = {
    id: `med_${Date.now()}`,
    name: body.name,
    dosage: body.dosage || null,
    form: body.form || 'TABLET',
    instructions: body.instructions || null,
    frequencyType: body.frequencyType || 'DAILY',
    timesOfDay: body.timesOfDay || [],
    daysOfWeek: body.daysOfWeek || null,
    startDate: body.startDate || new Date().toISOString().slice(0, 10),
    endDate: body.endDate || null,
    prescribedBy: body.prescribedBy || 'self',
    isActive: true,
  }
  prof.schedules.push(next)
  store[profile.id] = prof
  writeStore(store)
  return next
}

export async function updateMedicine(profile, id, patch) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  const idx = prof.schedules.findIndex((s) => s.id === id)
  if (idx === -1) return null
  prof.schedules[idx] = { ...prof.schedules[idx], ...patch }
  store[profile.id] = prof
  writeStore(store)
  return prof.schedules[idx]
}

export async function todaysDoses(profile) {
  await delay()
  if (!profile) return []
  const { prof } = ensure(profile)
  const out = []
  for (const s of prof.schedules) {
    if (!s.isActive) continue
    if (s.frequencyType === 'AS_NEEDED') continue
    if (s.frequencyType === 'WEEKLY') continue
    for (const hhmm of s.timesOfDay || []) {
      const [h, m] = hhmm.split(':').map((n) => Number(n))
      const scheduled = todayAt(h, m || 0)
      const intake = prof.intakes.find(
        (i) => i.medicineScheduleId === s.id && i.scheduledFor === scheduled,
      )
      out.push({
        intakeId: intake?.id || null,
        medicineScheduleId: s.id,
        medicineName: s.name,
        dosage: s.dosage,
        scheduledFor: scheduled,
        actualTakenAt: intake?.actualTakenAt || null,
        status: intake?.status || 'PENDING',
        note: intake?.note || null,
      })
    }
  }
  return out.sort((a, b) => (a.scheduledFor || '').localeCompare(b.scheduledFor || ''))
}

export async function recordIntake(profile, medicineId, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  // Upsert by (medicineId, scheduledFor)
  const idx = prof.intakes.findIndex(
    (i) => i.medicineScheduleId === medicineId && i.scheduledFor === body.scheduledFor,
  )
  const entry = {
    id: idx === -1 ? `intake_${Date.now()}` : prof.intakes[idx].id,
    medicineScheduleId: medicineId,
    scheduledFor: body.scheduledFor || null,
    actualTakenAt: body.actualTakenAt || (body.status === 'TAKEN' ? new Date().toISOString() : null),
    status: body.status,
    note: body.note || null,
  }
  if (idx === -1) prof.intakes.push(entry)
  else prof.intakes[idx] = entry
  store[profile.id] = prof
  writeStore(store)
  const sched = prof.schedules.find((s) => s.id === medicineId)
  return {
    intakeId: entry.id,
    medicineScheduleId: medicineId,
    medicineName: sched?.name,
    dosage: sched?.dosage,
    ...entry,
  }
}

export async function intakeHistory(profile, medicineId, from, to) {
  await delay()
  if (!profile) return []
  const { prof } = ensure(profile)
  const fromMs = from ? Date.parse(from) : 0
  const toMs = to ? Date.parse(to) : Number.POSITIVE_INFINITY
  const sched = prof.schedules.find((s) => s.id === medicineId)
  return prof.intakes
    .filter((i) => i.medicineScheduleId === medicineId)
    .filter((i) => {
      const t = Date.parse(i.scheduledFor || i.actualTakenAt || 0)
      return t >= fromMs && t < toMs
    })
    .map((i) => ({
      intakeId: i.id,
      medicineScheduleId: medicineId,
      medicineName: sched?.name,
      dosage: sched?.dosage,
      scheduledFor: i.scheduledFor,
      actualTakenAt: i.actualTakenAt,
      status: i.status,
      note: i.note,
    }))
    .sort((a, b) => (b.scheduledFor || '').localeCompare(a.scheduledFor || ''))
}

export async function recordSideEffect(profile, medicineId, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  const e = {
    id: `se_${Date.now()}`,
    medicineScheduleId: medicineId,
    description: body.description,
    severity: body.severity || 'MILD',
    recordedAt: new Date().toISOString(),
  }
  prof.sideEffects.push(e)
  store[profile.id] = prof
  writeStore(store)
  return e
}

export async function listSideEffects(profile, medicineId) {
  await delay(80)
  if (!profile) return []
  const { prof } = ensure(profile)
  return prof.sideEffects
    .filter((e) => e.medicineScheduleId === medicineId)
    .sort((a, b) => (b.recordedAt || '').localeCompare(a.recordedAt || ''))
}

export function __resetMedicinesMock() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}
